import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Mail, ArrowLeft, KeyRound, CheckCircle, Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import CinematicLayout from "../../components/CinematicLayout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: email, 2: code, 3: success
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const handleRequestReset = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Please enter your email");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API}/auth/password-reset/request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim().toLowerCase() })
      });

      if (response.ok) {
        toast.success("Reset code sent! Check your email.");
        setStep(2);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to send reset code");
      }
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (!resetCode.trim()) {
      toast.error("Please enter the reset code");
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error("Passwords don't match");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API}/auth/password-reset/confirm`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          token: resetCode.trim().toUpperCase(),
          new_password: newPassword 
        })
      });

      if (response.ok) {
        toast.success("Password reset successfully!");
        setStep(3);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Invalid or expired reset code");
      }
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <CinematicLayout>
      <div className="min-h-screen flex items-center justify-center p-6">
        <Card 
          className="w-full max-w-md"
          style={{
            background: 'linear-gradient(145deg, rgba(20, 15, 5, 0.95) 0%, rgba(15, 10, 5, 0.95) 100%)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 215, 0, 0.2)',
            boxShadow: '0 0 50px rgba(255, 215, 0, 0.1)'
          }}
        >
          <CardHeader className="text-center">
            <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ background: 'rgba(255, 215, 0, 0.15)' }}>
              <KeyRound size={32} className="text-[#FFD700]" />
            </div>
            <CardTitle className="text-2xl text-[#FFFFFF]" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              {step === 1 && "Reset Password"}
              {step === 2 && "Enter Reset Code"}
              {step === 3 && "Password Reset!"}
            </CardTitle>
            <p className="text-[#E5C558] text-sm mt-2">
              {step === 1 && "Enter your email to receive a reset code"}
              {step === 2 && "Check your email for the 8-character code"}
              {step === 3 && "Your password has been updated"}
            </p>
          </CardHeader>
          <CardContent>
            {step === 1 && (
              <form onSubmit={handleRequestReset} className="space-y-4">
                <div>
                  <Label className="text-[#E5C558]">Email Address</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-[#E5C558]" size={18} />
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="your@email.com"
                      className="pl-10 text-[#FFFFFF]"
                      style={{ background: 'rgba(26, 26, 26, 0.8)', borderColor: 'rgba(255, 215, 0, 0.2)' }}
                      required
                    />
                  </div>
                </div>
                
                <Button 
                  type="submit" 
                  className="w-full bg-gradient-to-r from-[#FFD700] to-[#D4AF37] text-[#0a0500] font-semibold"
                  disabled={loading}
                >
                  {loading ? <Loader2 className="animate-spin mr-2" size={18} /> : <Mail className="mr-2" size={18} />}
                  Send Reset Code
                </Button>
              </form>
            )}

            {step === 2 && (
              <form onSubmit={handleResetPassword} className="space-y-4">
                <div>
                  <Label className="text-[#E5C558]">Reset Code</Label>
                  <Input
                    type="text"
                    value={resetCode}
                    onChange={(e) => setResetCode(e.target.value.toUpperCase())}
                    placeholder="XXXXXXXX"
                    maxLength={8}
                    className="text-center text-xl tracking-widest text-[#FFFFFF] font-mono"
                    style={{ background: 'rgba(26, 26, 26, 0.8)', borderColor: 'rgba(255, 215, 0, 0.2)' }}
                    required
                  />
                </div>
                
                <div>
                  <Label className="text-[#E5C558]">New Password</Label>
                  <Input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="••••••••"
                    className="text-[#FFFFFF]"
                    style={{ background: 'rgba(26, 26, 26, 0.8)', borderColor: 'rgba(255, 215, 0, 0.2)' }}
                    required
                  />
                </div>
                
                <div>
                  <Label className="text-[#E5C558]">Confirm Password</Label>
                  <Input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="••••••••"
                    className="text-[#FFFFFF]"
                    style={{ background: 'rgba(26, 26, 26, 0.8)', borderColor: 'rgba(255, 215, 0, 0.2)' }}
                    required
                  />
                </div>
                
                <Button 
                  type="submit" 
                  className="w-full bg-gradient-to-r from-[#FFD700] to-[#D4AF37] text-[#0a0500] font-semibold"
                  disabled={loading}
                >
                  {loading ? <Loader2 className="animate-spin mr-2" size={18} /> : <KeyRound className="mr-2" size={18} />}
                  Reset Password
                </Button>
                
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="w-full text-center text-sm text-[#E5C558] hover:text-[#FFD700]"
                >
                  Didn't receive code? Try again
                </button>
              </form>
            )}

            {step === 3 && (
              <div className="text-center space-y-4">
                <div className="w-20 h-20 rounded-full mx-auto flex items-center justify-center" style={{ background: 'rgba(34, 197, 94, 0.2)' }}>
                  <CheckCircle size={40} className="text-[#22C55E]" />
                </div>
                <p className="text-[#E5C558]">You can now log in with your new password.</p>
                <Button 
                  onClick={() => navigate("/staff/login")}
                  className="w-full bg-gradient-to-r from-[#FFD700] to-[#D4AF37] text-[#0a0500] font-semibold"
                >
                  Go to Login
                </Button>
              </div>
            )}

            <div className="mt-6 pt-4 border-t border-[#27272A] text-center">
              <Link to="/staff/login" className="text-[#E5C558] hover:text-[#FFD700] text-sm flex items-center justify-center gap-2">
                <ArrowLeft size={16} />
                Back to Login
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </CinematicLayout>
  );
};

export default ForgotPassword;
